// Copyright 2023, Durable
/**
 * Authentication utilities and components.
 */
import {
  ApiError,
  OpenAPI,
  UserService,
  ViewUser
} from "@durable/durable-client";
import axios from "axios";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import useLocalStorageState from "use-local-storage-state";
import { logging } from "../../logging";
import { LocalStorageLocation } from "../../LocalStorage";

export type DurableProfile = {
  userId: string;
  token: string;
};

/**
 * Type of the profile context state.
 *
 * Profile context is the current "active" profiles that a user can have,
 * and how the user authentication context is constructed. This uses local
 * storage to cache the profiles across user sessions; note, this is not
 * recommended (it's recommended to use httpOnly cookies instead).
 *
 * The rationale for having profile switching is mainly:
 * - it will pay for itself very quickly during development time, and
 * - implementing it later will be extremely onerous, in case users want it.
 */
export type ProfileContextType = {
  currentProfile?: DurableProfile;
  profiles: Map<string, DurableProfile>;
  setCurrentProfile: (userId?: string) => Promise<void>;
  updateProfile: (userId: string, token: string) => Promise<void>;
  removeProfile: (userId: string) => Promise<void>;
};
/**
 * Type of the auth context state.
 *
 * Auth context is the current, active credentials and user profile that is
 * being used by durable. Logging in or setting the tokens here will override
 * the global Durable OpenAPI client authentication, which allows for cleaner
 * code around API calls.
 */
export type AuthContextType = {
  user?: ViewUser;
  token?: string;
  handleLogin: (username: string, password: string) => Promise<void>;
  handleTokenLogin: (token: string) => Promise<void>;
  handleLogout: () => Promise<void>;
  refreshUser: () => Promise<ViewUser>;
};

/**
 * Profile context JSX object.
 */
export const ProfileContext = createContext<ProfileContextType>(null);
/**
 * Authentication context JSX object.
 */
export const AuthContext = createContext<AuthContextType>(null);

/**
 * Hook to use the current user profile context.
 */
export const useProfiles = () => {
  return useContext(ProfileContext);
};

/**
 * Hook to use the current authentication context.
 */
export const useAuth = () => {
  return useContext(AuthContext);
};

/**
 * Add a protected route.
 *
 * What this means is - if no token is currently set, this will kick the user
 * back to login.
 */
export function ProtectedRoute() {
  const { token } = useAuth();
  const location = useLocation();
  const { currentProfile } = useProfiles();
  // NOTE - the token expiration should be checked here as well.
  // If returnTo is populated with a route, after login will return the user
  // there.
  if (!token || !currentProfile) {
    return (
      <Navigate to={`/app/login?returnTo=${location.pathname}`} replace />
    );
  }
  return <Outlet />;
}

/**
 * Method to fetch a user from a profile.
 */
const fetchUser = async (profile: DurableProfile): Promise<ViewUser> => {
  const result = await axios.get("/user", {
    headers: { Authorization: `Bearer ${profile.token}` }
  });
  return result.data;
};

/**
 * Asynchronously fetch users from the provided profiles and call the callback
 * when finished.
 *
 * If user fetching auth fails, then remove that profile from the profile
 * context.
 */
export const fetchUsersFromProfiles = async (
  profiles: DurableProfile[],
  setUsers: (users: ViewUser[]) => void,
  removeProfile: (userId: string) => void,
  currentUserId?: string
) => {
  const mappedProfiles = await Promise.all(
    profiles.map(async (prf) => {
      try {
        return await fetchUser(prf);
      } catch (err) {
        logging.error(err);
        removeProfile(prf.userId);
      }
    })
  );
  setUsers(mappedProfiles.filter((prf) => prf.id != currentUserId));
};

/**
 * Profile storage object type. Contains the selected user and the sroted user
 * profiles.
 */
type ProfileStore = {
  selectedUser?: string;
  users: { [key: string]: DurableProfile };
};

/**
 * Hook to handle dealing with the user storage.
 */
function useProfileStore(): ProfileContextType {
  const [userStore, setUserStore] = useLocalStorageState<ProfileStore>(
    LocalStorageLocation.DurableUsers,
    {
      defaultValue: { selectedUser: null, users: {} }
    }
  );

  /**
   * Determine the current user id from the userstore.
   *
   * Memoized to prevent repeated calls on route changes.
   */
  const currentUserId = useMemo(() => {
    return userStore.selectedUser;
  }, [userStore]);
  /**
   * Update user component of the hook. Will update the userstore to add the
   * new user and token to the store.
   */
  const updateUser = async (userId: string, token: string) =>
    setUserStore((oldUserStore) => {
      oldUserStore.users[userId] = { userId, token };
      return oldUserStore;
    });
  const setCurrentUserId = async (userId?: string) => {
    setUserStore((oldUserStore) => {
      oldUserStore.selectedUser = userId;
      return oldUserStore;
    });
  };

  const removeUser = async (userId: string) => {
    setUserStore((oldUserStore) => {
      delete oldUserStore.users[userId];
      return oldUserStore;
    });
  };

  const users = useMemo(() => userStore.users, [userStore]);

  return {
    currentProfile: userStore.users[currentUserId],
    setCurrentProfile: setCurrentUserId,
    profiles: new Map(Object.entries(users)),
    updateProfile: updateUser,
    removeProfile: removeUser
  };
}

/**
 * Profile context value provider.
 *
 * Uses the profile storage mechanism to initialize the profile context
 * used by downstream components.
 */
export function useProfileContext() {
  const {
    currentProfile,
    setCurrentProfile,
    profiles: users,
    updateProfile: updateUser,
    removeProfile: removeUser
  } = useProfileStore();
  const userContext = useMemo<ProfileContextType>(
    () => ({
      currentProfile: currentProfile,
      profiles: users,
      setCurrentProfile: setCurrentProfile,
      updateProfile: updateUser,
      removeProfile: removeUser
    }),
    [currentProfile, users, removeUser, setCurrentProfile, updateUser]
  );

  return userContext;
}

/**
 * Set up the authentication context for durable. See AuthContextType
 * for details.
 */
export function useAuthContext(): AuthContextType {
  const {
    currentProfile,
    setCurrentProfile: setCurrentUser,
    updateProfile: updateUser,
    removeProfile: removeUser
  } = useProfiles();
  const [user, setUser] = useState<ViewUser>(null);

  /**
   * Set the user id and token in the profile context, and set the
   * OpenAPI token to the new token.
   *
   * @param userId
   * @param token
   */
  const setUserIdAndToken = useCallback(
    (userId: string, token: string) => {
      // Update the profile with the new token.
      updateUser(userId, token);
      setCurrentUser(userId);
      OpenAPI.TOKEN = token;
    },
    [setCurrentUser, updateUser]
  );

  const refreshUser = useCallback(async () => {
    try {
      // Fetch fresh user data from your API
      const userData = await UserService.getCurrentUser();
      setUser(userData);
      return userData;
    } catch (err) {
      logging.error((err as ApiError).body);
    }
  }, [setUser]);

  /**
   * Log in and override the OPENAPI token on success.
   * @param username
   * @param password
   */
  const handleLogin = useCallback(
    async (username: string, password: string) => {
      try {
        const authResponse = await UserService.login({
          username: username,
          password: password
        });
        setUserIdAndToken(authResponse.user_id, authResponse.token);
      } catch (err) {
        logging.error((err as ApiError).body);
      }
    },
    [setUserIdAndToken]
  );

  /**
   * Log out the currently logged in user.
   * Set the current user in the profile context to null.
   */
  const handleLogout = useCallback(async () => {
    if (!currentProfile) {
      return;
    }
    removeUser(currentProfile.userId);
    setCurrentUser(null);
    // Don't use `currentProfile` since that gets set indirectly via
    // `setCurrentUser` and triggers a null error.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeUser, setCurrentUser]);

  /**
   * Log in using a JWT token and override the OPENAPI token on success.
   * @param token
   */
  const handleTokenLogin = useCallback(
    async (token: string) => {
      // Log out the current user if they are logged in.
      await handleLogout();
      logging.info("Logging in with token");
      try {
        const authResponse = await UserService.login({
          username: "",
          password: token
        });
        setUserIdAndToken(authResponse.user_id, authResponse.token);
        logging.info("Logged in with token");
      } catch (err) {
        logging.error(
          "Error logging in with token: " + (err as ApiError).body
        );
      }
    },
    [setUserIdAndToken, handleLogout]
  );

  /**
   * Asynchronously load the user from the profile authentication.
   */
  useEffect(() => {
    if (currentProfile) {
      UserService.getCurrentUser()
        .then(setUser)
        .catch(() => handleLogout());
    }
  }, [currentProfile, handleLogout]);

  OpenAPI.TOKEN = useMemo(
    () => (currentProfile ? currentProfile.token : ""),
    [currentProfile]
  );

  const authValue = useMemo(
    () => ({
      user,
      token: currentProfile?.token,
      handleLogin,
      handleTokenLogin,
      handleLogout,
      refreshUser
    }),
    [
      user,
      currentProfile,
      handleLogin,
      handleTokenLogin,
      handleLogout,
      refreshUser
    ]
  );
  return authValue;
}
